import { supabase } from '@/integrations/supabase/client';
import { countActiveBusinesses } from '@/modules/shared/core/businessSlots';
import type { UserReadPort, AuthSessionReadPort, MemberProfile } from '../core/types';

// This adapter now treats business IDs as the only live ownership scope for business-admin lookups.
function extractBusinessIds(rows: Array<{ business_id?: string | null }>): string[] {
  return Array.from(
    new Set(
      rows
        .map((row) => row.business_id)
        .filter((businessId): businessId is string => Boolean(businessId)),
    ),
  );
}

function extractMemberIds(memberIds: unknown): string[] {
  if (!Array.isArray(memberIds)) {
    return [];
  }

  return memberIds
    .map((memberId) => {
      if (typeof memberId === 'string') {
        return memberId;
      }

      if (memberId && typeof memberId === 'object') {
        const record = memberId as Record<string, unknown>;
        const candidate = record.get_facility_member_ids ?? record.user_id ?? record.id;
        if (typeof candidate === 'string' && candidate.trim()) {
          return candidate;
        }
      }

      return null;
    })
    .filter((memberId): memberId is string => Boolean(memberId));
}

export const supabaseUserReadPort: UserReadPort = {
  async getFacilityMemberProfiles(facilityId: string) {
    const { data: memberIds, error: memberIdsError } = await supabase.rpc('get_facility_member_ids', {
      _facility_id: facilityId,
    });

    if (memberIdsError) throw memberIdsError;

    const resolvedMemberIds = extractMemberIds(memberIds);

    if (resolvedMemberIds.length > 0) {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, full_name, email, phone')
        .in('user_id', resolvedMemberIds);

      if (profilesError) throw profilesError;
      return (profiles || []) as MemberProfile[];
    }

    return [];
  },

  async getUserBusinessAdminCount(userId: string): Promise<number> {
    const { data: roleRows, error: rolesError } = await supabase
      .from('user_roles')
      .select('business_id')
      .eq('user_id', userId)
      .eq('role', 'business_admin');

    if (rolesError) throw rolesError;

    const businessIds = extractBusinessIds(roleRows || []);
    if (businessIds.length === 0) {
      return 0;
    }

    const activeFacilityRows: Array<{ id: string; is_active: boolean }> = [];

    if (businessIds.length > 0) {
      const { data, error } = await supabase
        .from('facilities')
        .select('id, is_active')
        .eq('is_active', true)
        .in('business_id', businessIds);

      if (error) throw error;
      activeFacilityRows.push(...((data || []) as Array<{ id: string; is_active: boolean }>));
    }

    const uniqueActiveFacilities = Array.from(
      new Map(activeFacilityRows.map((facility) => [facility.id, facility])).values(),
    );

    return countActiveBusinesses(uniqueActiveFacilities);
  },

  async inviteMemberByEmail(params) {
    const { data, error } = await supabase.functions.invoke('invite-user-by-email', {
      body: {
        facilityId: params.facilityId,
        email: params.email,
        fullName: params.fullName,
        phone: params.phone,
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);

    return {
      userId: data.userId,
      fullName: data.fullName || params.fullName || null,
      email: data.email || params.email,
      phone: data.phone || params.phone || null,
      existing: !!data.existing,
    };
  },
};

export const supabaseAuthSessionReadPort: AuthSessionReadPort = {
  async getProfile(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // not found
      throw error;
    }
    return data;
  },

  async getUserRoles(userId: string) {
    const { data, error } = await supabase
      .from('user_roles')
      .select('*')
      .eq('user_id', userId);

    if (error) throw error;
    return (data || []).map((r) => ({
      id: r.id,
      role: r.role,
      business_id: r.business_id,
      facility_id: null,
    }));
  },
};

